import type { Timeframe } from '@shared/types/candle'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import type { ChartTemplate } from '@/stores/templateStore'
import type { ActiveIndicator, ChartPanel, ChartType } from '@/types/chart'
import { DEFAULT_PANEL } from '@/types/chart'

// Varied symbols so multi-panel layouts look useful out of the box.
const DEFAULT_SYMBOLS = [
  'BTC',
  'ETH',
  'AAPL',
  '^NSEI',
  'RELIANCE.NS',
  'MSFT',
  'SOL',
  'NVDA',
  'TSLA',
  'AMZN',
  'BNB',
  'GOOG',
  'SPY',
  'QQQ',
  'INFY.NS',
  'TCS.NS',
]

function makePanel(id: string, index = 0): ChartPanel {
  return {
    ...DEFAULT_PANEL,
    id,
    symbol: DEFAULT_SYMBOLS[index % DEFAULT_SYMBOLS.length] ?? 'BTC',
  }
}

interface IndicatorUpdate {
  params?: Record<string, number | string | boolean>
  colors?: Record<string, string>
}

interface ChartStore {
  panels: Record<string, ChartPanel>
  activePanelId: string

  // ── Panel actions ─────────────────────────────────────────────────────────
  initPanels: (count: number) => void
  setActivePanel: (id: string) => void
  setSymbol: (panelId: string, symbol: string) => void
  setTimeframe: (panelId: string, tf: Timeframe) => void
  setChartType: (panelId: string, type: ChartType) => void

  // ── Built-in indicator actions ─────────────────────────────────────────────
  addIndicator: (panelId: string, indicator: Omit<ActiveIndicator, 'instanceId'>) => void
  removeIndicator: (panelId: string, instanceId: string) => void
  updateIndicator: (panelId: string, instanceId: string, updates: IndicatorUpdate) => void
  toggleIndicatorEnabled: (panelId: string, instanceId: string) => void
  reorderIndicators: (panelId: string, fromIndex: number, toIndex: number) => void

  // ── Custom / Pine indicator actions ───────────────────────────────────────
  attachCustomIndicator: (panelId: string, definitionId: string) => string
  detachCustomIndicator: (panelId: string, instanceId: string) => void
  toggleCustomIndicatorEnabled: (panelId: string, instanceId: string) => void
  reorderCustomIndicators: (panelId: string, fromIndex: number, toIndex: number) => void

  // ── Template actions ───────────────────────────────────────────────────────
  applyTemplate: (panelId: string, template: ChartTemplate) => void
}

export const useChartStore = create<ChartStore>()(
  persist(
    immer((set) => ({
      panels: { 'panel-1': makePanel('panel-1') },
      activePanelId: 'panel-1',

      // ── Panel ──────────────────────────────────────────────────────────────

      initPanels: (count) =>
        set((s) => {
          const next: Record<string, ChartPanel> = {}
          for (let i = 1; i <= count; i++) {
            const id = `panel-${i}`
            next[id] = s.panels[id] ?? makePanel(id, i - 1)
            // Ensure new field exists on migrated panels
            if (!next[id].customInstances) next[id].customInstances = []
          }
          s.panels = next
          if (!next[s.activePanelId]) s.activePanelId = 'panel-1'
        }),

      setActivePanel: (id) =>
        set((s) => {
          s.activePanelId = id
        }),

      setSymbol: (panelId, symbol) =>
        set((s) => {
          if (s.panels[panelId]) s.panels[panelId].symbol = symbol
        }),

      setTimeframe: (panelId, tf) =>
        set((s) => {
          if (s.panels[panelId]) s.panels[panelId].timeframe = tf
        }),

      setChartType: (panelId, type) =>
        set((s) => {
          if (s.panels[panelId]) s.panels[panelId].chartType = type
        }),

      // ── Built-in ──────────────────────────────────────────────────────────

      addIndicator: (panelId, indicator) =>
        set((s) => {
          s.panels[panelId]?.indicators.push({
            ...indicator,
            instanceId: crypto.randomUUID(),
            enabled: true,
          })
        }),

      removeIndicator: (panelId, instanceId) =>
        set((s) => {
          const panel = s.panels[panelId]
          if (panel) panel.indicators = panel.indicators.filter((i) => i.instanceId !== instanceId)
        }),

      updateIndicator: (panelId, instanceId, updates) =>
        set((s) => {
          const ind = s.panels[panelId]?.indicators.find((i) => i.instanceId === instanceId)
          if (!ind) return
          if (updates.params) ind.params = { ...ind.params, ...updates.params }
          if (updates.colors) ind.colors = { ...(ind.colors ?? {}), ...updates.colors }
        }),

      toggleIndicatorEnabled: (panelId, instanceId) =>
        set((s) => {
          const ind = s.panels[panelId]?.indicators.find((i) => i.instanceId === instanceId)
          if (ind) ind.enabled = !(ind.enabled ?? true)
        }),

      reorderIndicators: (panelId, fromIndex, toIndex) =>
        set((s) => {
          const arr = s.panels[panelId]?.indicators
          if (!arr || fromIndex === toIndex) return
          const [item] = arr.splice(fromIndex, 1)
          if (item) arr.splice(toIndex, 0, item)
        }),

      // ── Custom / Pine ─────────────────────────────────────────────────────

      attachCustomIndicator: (panelId, definitionId) => {
        const instanceId = crypto.randomUUID()
        set((s) => {
          const panel = s.panels[panelId]
          if (!panel) return
          if (!panel.customInstances) panel.customInstances = []
          panel.customInstances.push({ instanceId, definitionId, enabled: true })
        })
        return instanceId
      },

      detachCustomIndicator: (panelId, instanceId) =>
        set((s) => {
          const panel = s.panels[panelId]
          if (panel) {
            panel.customInstances = (panel.customInstances ?? []).filter(
              (i) => i.instanceId !== instanceId
            )
          }
        }),

      toggleCustomIndicatorEnabled: (panelId, instanceId) =>
        set((s) => {
          const inst = (s.panels[panelId]?.customInstances ?? []).find(
            (i) => i.instanceId === instanceId
          )
          if (inst) inst.enabled = !inst.enabled
        }),

      reorderCustomIndicators: (panelId, fromIndex, toIndex) =>
        set((s) => {
          const arr = s.panels[panelId]?.customInstances
          if (!arr || fromIndex === toIndex) return
          const [item] = arr.splice(fromIndex, 1)
          if (item) arr.splice(toIndex, 0, item)
        }),

      // ── Templates ─────────────────────────────────────────────────────────

      applyTemplate: (panelId, template) =>
        set((s) => {
          const panel = s.panels[panelId]
          if (!panel) return
          panel.chartType = template.chartType
          panel.timeframe = template.timeframe
          panel.indicators = template.indicators.map((ind) => ({
            ...ind,
            instanceId: crypto.randomUUID(),
            enabled: true,
          }))
        }),
    })),
    {
      name: 'mhp-charts',
      version: 2,
      migrate: (persisted, fromVersion) => {
        const state = persisted as { panels?: Record<string, Record<string, unknown>> }
        for (const panel of Object.values(state.panels ?? {})) {
          // v0→v1: assign instanceId to legacy indicators
          if (fromVersion === 0) {
            const inds = panel['indicators'] as Array<Record<string, unknown>> | undefined
            panel['indicators'] = (inds ?? []).map((ind) =>
              ind['instanceId'] ? ind : { ...ind, instanceId: crypto.randomUUID() }
            )
          }
          // v1→v2: add customInstances + enabled fields
          if (fromVersion <= 1) {
            if (!panel['customInstances']) panel['customInstances'] = []
            const inds = panel['indicators'] as Array<Record<string, unknown>> | undefined
            if (inds) {
              for (const ind of inds) {
                if (ind['enabled'] === undefined) ind['enabled'] = true
              }
            }
          }
        }
        return persisted
      },
    }
  )
)
